import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { fireAudit } from '../utils/auditClient';

const Others = () => {
  const today = new Date().toISOString().split('T')[0];
  const { userData, selectedCenter } = useUser();
  const userId = userData?.userId;

  const [loading, setLoading] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    date: today,
    academy: null,
    sport: null,
    venueQr: '',
    cash: '',
    comments: '',
    name: '',
    contact: '',
    companyName: ''
  });

  const [academies, setAcademies] = useState([]);
  const [academyOptions, setAcademyOptions] = useState([]);
  const [sportsOptions, setSportsOptions] = useState([]);
  const token = localStorage.getItem('auth-token');

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  const onlyDigits = (v) => v.replace(/\D/g, '');
  const toAmount = (v) => (v === '' ? '' : /^\d+$/.test(String(v)) ? Number(v) : '');

  useEffect(() => {
    const fetchAcademies = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL_ACADEMIES, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });
        const fetchedAcademies = response?.data?.data?.academies || [];
        const filtered =
          userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)
            ? fetchedAcademies.filter(a => userData.academyIds.includes(a.id))
            : fetchedAcademies;
        setAcademies(filtered);
        setAcademyOptions(filtered.map(a => ({ label: a.name, value: a.id })));
        setOptionsLoaded(true);
      } catch (error) {
        console.error('Error fetching academies:', error);
      }
    };
    fetchAcademies();
  }, [userData?.userType, userData?.academyIds]);

  useEffect(() => {
    if (!selectedCenter || academies.length === 0) return;
    const selected = academies.find(a => a.id === Number(selectedCenter));
    if (!selected) return;
    const academyOpt = { label: selected.name, value: selected.id };
    setFormData(prev => ({ ...prev, academy: academyOpt }));
    const sports = (selected.sports || []).map(s => ({
      label: s.name,
      value: s.sport_id,
      image: s.sport_pic
    }));
    setSportsOptions(sports);
  }, [selectedCenter, academies]);

  const handleAcademyChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, academy: selectedOption, sport: null }));
    const selected = academies.find(a => a.id === selectedOption?.value);
    const sports = (selected?.sports || []).map(s => ({
      label: s.name,
      value: s.sport_id,
      image: s.sport_pic
    }));
    setSportsOptions(sports);
    baselineRef.current = null;
    initializedBaselineRef.current = false;
  };

  const handleSportChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, sport: selectedOption }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contact') {
      setFormData(prev => ({ ...prev, contact: onlyDigits(value).slice(0, 10) }));
      return;
    }
    if (name === 'venueQr' || name === 'cash') {
      const amt = toAmount(value);
      if (amt === '' && value !== '') return;
      setFormData(prev => ({ ...prev, [name]: value === '' ? '' : amt }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const buildSubmitPayload = useCallback(() => {
    const vAmt = Number(formData.venueQr || 0);
    const cAmt = Number(formData.cash || 0);
    return {
      userId,
      name: (formData.name || '').trim(),
      mobileNumber: formData.contact,
      companyName: (formData.companyName || '').trim() || undefined,
      sportId: formData.sport?.value ?? null,
      academyId: formData.academy?.value ?? null,
      comments: (formData.comments || '').trim(),
      date: formData.date,
      venueQr: vAmt,
      cash: cAmt,
      amountReceived: vAmt + cAmt,
    };
  }, [formData, userId]);

  const redactForAudit = useCallback((payload) => {
    if (!payload) return payload;
    const copy = JSON.parse(JSON.stringify(payload));
    if (copy.mobileNumber) copy.mobileNumber = '********';
    if (copy.comments) copy.comments = '********';
    return copy;
  }, []);

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    if (!optionsLoaded) return;
    baselineRef.current = buildSubmitPayload();
    initializedBaselineRef.current = true;
  }, [optionsLoaded, buildSubmitPayload]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { date, academy, sport, venueQr, cash, comments, name, contact, companyName } = formData;

    if (!name.trim()) {
      Swal.fire({ icon: 'error', title: 'Name Required!', text: 'Please enter a name.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!contact || contact.length !== 10) {
      Swal.fire({ icon: 'error', title: 'Invalid Contact!', text: 'Enter a 10-digit phone number.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!date) {
      Swal.fire({ icon: 'error', title: 'Date Missing!', text: 'Please select a date.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!comments.trim()) {
      Swal.fire({ icon: 'error', title: 'Comments Required!', text: 'Please add comments before submitting.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!academy) {
      Swal.fire({ icon: 'error', title: 'Academy Not Selected!', text: 'Please select an academy.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!sport) {
      Swal.fire({ icon: 'error', title: 'Sport Not Selected!', text: 'Please select a sport.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    const vAmt = Number(venueQr || 0);
    const cAmt = Number(cash || 0);
    if (vAmt <= 0 && cAmt <= 0) {
      Swal.fire({ icon: 'error', title: 'Payment Missing!', text: 'Enter Venue QR or Cash amount (> 0).', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    const payload = {
      userId,
      name: name.trim(),
      mobileNumber: contact,
      companyName: companyName?.trim() || undefined,
      sportId: sport.value,
      academyId: academy.value,
      comments: comments.trim(),
      date,
      venueQr: vAmt,
      cash: cAmt,
    };

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || {};

    setLoading(true);
    Swal.fire({
      title: 'Submitting...',
      text: 'Please wait while we process your entry.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const resp = await axios.post(process.env.REACT_APP_API_URL_OTHERS, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      Swal.close();
      Swal.fire({ icon: 'success', title: 'Success!', text: 'Others entry added successfully.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

      const affected_id =
        resp?.data?.data?.id ??
        resp?.data?.id ??
        `${payload.academyId}-${payload.mobileNumber}-${payload.date}`;

      fireAudit({
        entity: 'others',
        affected_id,
        action: 'create',
        businessApi: process.env.REACT_APP_API_URL_OTHERS,
        businessMethod: 'POST',
        oldPayload: redactForAudit(oldPayload),
        newPayload: redactForAudit(newPayload),
        section: 'Centre Management',
        subSection: 'Walkin Sales(Others)',
        extra: { page: 'Others', academyId: payload.academyId, sportId: payload.sportId, date: payload.date },
      });

      baselineRef.current = newPayload;

      setFormData(prev => ({
        ...prev,
        venueQr: '',
        cash: '',
        comments: '',
        name: '',
        contact: '',
        companyName: ''
      }));
    } catch (error) {
      console.error('API Error:', error);
      Swal.close();
      Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to submit entry.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ margin: 'auto' }}>
      <form onSubmit={handleSubmit}>
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="academy"
            options={academyOptions}
            value={formData.academy}
            onChange={handleAcademyChange}
            placeholder="Select Academy"
            className={`walkin-form-dropdown ${formData.academy ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (p) => ({ ...p, padding: '5px' }),
              placeholder: (p) => ({ ...p, color: 'black' }),
            }}
          />
          {formData.academy && <label htmlFor="academy">Academy</label>}
        </div>

        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="sport"
            options={sportsOptions}
            value={formData.sport}
            onChange={handleSportChange}
            placeholder="Select Sport"
            className={`walkin-form-dropdown ${formData.sport ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (p) => ({ ...p, padding: '5px' }),
              placeholder: (p) => ({ ...p, color: 'black' }),
            }}
          />
          {formData.sport && <label htmlFor="sport">Sport</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`walkin-form ${formData.name ? 'has-value' : ''}`}
            placeholder="Name"
          />
          {formData.name && <label htmlFor="name">Name</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            inputMode="numeric"
            maxLength={10}
            className={`walkin-form ${formData.contact ? 'has-value' : ''}`}
            placeholder="Contact Number"
          />
          {formData.contact && <label htmlFor="contact">Contact Number</label>}
        </div>

        <div>
          <label style={{ margin: 0 }}>Amount Received:</label>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="venueQr"
              value={formData.venueQr === 0 ? '0' : formData.venueQr || ''}
              onChange={handleChange}
              inputMode="numeric"
              className={`walkin-form ${formData.venueQr !== '' ? 'has-value' : ''}`}
              style={{ padding: '12px 10px' }}
              placeholder="Venue QR (Amount in Rs.)"
            />
            {formData.venueQr !== '' && <label htmlFor="venueQr">Venue QR</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="cash"
              value={formData.cash === 0 ? '0' : formData.cash || ''}
              onChange={handleChange}
              inputMode="numeric"
              className={`walkin-form ${formData.cash !== '' ? 'has-value' : ''}`}
              style={{ padding: '12px 10px' }}
              placeholder="Cash (Amount in Rs.)"
            />
            {formData.cash !== '' && <label htmlFor="cash">Cash</label>}
          </div>
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            className={`walkin-form ${formData.comments ? 'has-value' : ''}`}
            style={{ padding: '12px 10px' }}
            placeholder="Comments (mandatory)"
            maxLength={300}
          />
          {formData.comments && <label htmlFor="comments">Comments</label>}
        </div>

        <button className="submit-button" type="submit" disabled={loading}>
          {loading ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default Others;
